import { Rng, WeaponSlot, type FireKind, type MatchPlayerStats, type ModeId, type RewardBundle } from '@vm/shared';
import { WEAPON_BY_ID } from '../weapons';

export interface QuestTemplate {
  id: string;
  kind: 'daily' | 'weekly';
  title: string;
  description: string;
  /** See `questMetricValue` for the supported metrics. */
  metric: string;
  goal: number;
  reward: RewardBundle;
  /** Only offered while one of these modes is enabled. */
  modes?: ModeId[];
}

export const QUESTS_PER_DAY = 3;
export const QUESTS_PER_WEEK = 3;
/** Quest rerolls per UTC day (daily or weekly quests). */
export const QUEST_REROLLS_PER_DAY = 1;

const daily = (id: string, title: string, description: string, metric: string, goal: number, coins: number, bpXp: number, modes?: ModeId[]): QuestTemplate => ({
  id,
  kind: 'daily',
  title,
  description,
  metric,
  goal,
  reward: { coins, bpXp },
  ...(modes ? { modes } : {}),
});

const weekly = (id: string, title: string, description: string, metric: string, goal: number, coins: number, bpXp: number, gems = 0, modes?: ModeId[]): QuestTemplate => ({
  id,
  kind: 'weekly',
  title,
  description,
  metric,
  goal,
  reward: gems ? { coins, gems, bpXp } : { coins, bpXp },
  ...(modes ? { modes } : {}),
});

export const DAILY_QUESTS: QuestTemplate[] = [
  daily('d_kills', 'Takedown Duty', 'Get 15 takedowns.', 'kills', 15, 150, 300),
  daily('d_headshots', 'Sharpshooter', 'Land 5 headshot takedowns.', 'headshots', 5, 150, 300),
  daily('d_wins', 'Winning Streak', 'Win 2 matches.', 'wins', 2, 200, 350),
  daily('d_matches', 'Reporting for Duty', 'Finish 3 matches.', 'matches', 3, 120, 300),
  daily('d_assists', 'Team Player', 'Get 6 assists.', 'assists', 6, 120, 300),
  daily('d_voxels', 'Demolition Crew', 'Destroy 2,000 voxels.', 'voxels', 2000, 150, 300),
  daily('d_built', 'Architect', 'Build 300 voxels.', 'built', 300, 150, 300, ['siege', 'zombies']),
  daily('d_primary', 'Front Line', 'Get 10 takedowns with primary weapons.', 'kills:primary', 10, 150, 300),
  daily('d_backup', 'Sidearm Specialist', 'Get 5 takedowns with backup weapons.', 'kills:backup', 5, 150, 300),
  daily('d_melee', 'Up Close', 'Get 3 melee takedowns.', 'kills:melee', 3, 150, 300),
  daily('d_heavy', 'Heavy Hitter', 'Get 5 takedowns with heavy weapons.', 'kills:heavy', 5, 150, 300),
  daily('d_sniper', 'Long Range', 'Get 5 takedowns with sniper rifles.', 'kills:sniper', 5, 150, 300),
  daily('d_projectile', 'Boom Time', 'Get 5 takedowns with launchers and explosives.', 'kills:projectile', 5, 150, 300),
  daily('d_objectives', 'Objective Focus', 'Complete 3 objectives (captures, flags, cores).', 'objectives', 3, 180, 350, ['dom', 'ctf', 'demolition', 'siege']),
  daily('d_collapse', 'Timber!', 'Take down an enemy with a collapsing structure.', 'collapse', 1, 200, 350),
  daily('d_zombies', 'Cleanup Crew', 'Clear 40 corrupted marines in Corruption Survival.', 'zombies', 40, 150, 300, ['zombies']),
  daily('d_dom', 'Hold the Line', 'Play 2 Domination matches.', 'mode:dom', 2, 150, 300, ['dom']),
  daily('d_ctf', 'Flag Runner', 'Play 2 Capture the Flag matches.', 'mode:ctf', 2, 150, 300, ['ctf']),
  daily('d_minutes', 'Boots on the Ground', 'Play for 20 minutes.', 'minutes', 20, 120, 300),
];

/**
 * Every weekly quest pays 10 gems (390 a 13-week season). With the daily login streak and the free
 * track, a player who logs in every day earns about two passes' worth a season (the admin Seasons
 * page shows the estimate per season).
 */
export const WEEKLY_QUESTS: QuestTemplate[] = [
  weekly('w_kills', 'Marine of the Week', 'Get 120 takedowns.', 'kills', 120, 600, 1500, 10),
  weekly('w_wins', 'Champion', 'Win 12 matches.', 'wins', 12, 700, 1500, 10),
  weekly('w_matches', 'Veteran', 'Finish 20 matches.', 'matches', 20, 500, 1500, 10),
  weekly('w_headshots', 'Eagle Eye', 'Land 40 headshot takedowns.', 'headshots', 40, 600, 1500, 10),
  weekly('w_voxels', 'Wrecking Ball', 'Destroy 25,000 voxels.', 'voxels', 25000, 600, 1500, 10),
  weekly('w_collapse', 'Structural Failure', 'Get 5 collapse takedowns.', 'collapse', 5, 700, 1500, 10),
  weekly('w_objectives', 'Mission Commander', 'Complete 25 objectives.', 'objectives', 25, 700, 1500, 10, ['dom', 'ctf', 'demolition', 'siege']),
  weekly('w_sniper', 'Ghost', 'Get 40 takedowns with sniper rifles.', 'kills:sniper', 40, 600, 1500, 10),
  weekly('w_melee', 'Brawler', 'Get 25 melee takedowns.', 'kills:melee', 25, 600, 1500, 10),
  weekly('w_streak', 'Unstoppable', 'Reach a 7-takedown streak in one match.', 'streak', 7, 700, 1500, 10),
  weekly('w_zombies', 'Purifier', 'Clear 300 corrupted marines.', 'zombies', 300, 600, 1500, 10, ['zombies']),
  weekly('w_ctf_wins', 'Flag Bearer', 'Win 5 Capture the Flag matches.', 'wins:ctf', 5, 700, 1500, 10, ['ctf']),
  weekly('w_br', 'Drop Zone', 'Play 5 Battle Royale matches.', 'mode:br', 5, 600, 1500, 10, ['br']),
];

export const QUEST_TEMPLATE_BY_ID: Record<string, QuestTemplate> = Object.fromEntries([...DAILY_QUESTS, ...WEEKLY_QUESTS].map((q) => [q.id, q]));

/**
 * Deterministically picks `count` distinct templates from `pool` for a seed (e.g. hash of user +
 * period). Templates in `exclude` or needing only disabled modes are skipped.
 */
export function pickQuests(pool: readonly QuestTemplate[], count: number, seed: number, opts: { exclude?: ReadonlySet<string>; enabledModes?: readonly ModeId[] } = {}): QuestTemplate[] {
  const candidates = pool.filter((q) => !opts.exclude?.has(q.id) && (!q.modes || !opts.enabledModes || q.modes.some((m) => opts.enabledModes!.includes(m))));
  return new Rng(seed).shuffle([...candidates]).slice(0, count);
}

const SLOT_BY_NAME: Record<string, number> = {
  primary: WeaponSlot.Primary,
  backup: WeaponSlot.Backup,
  melee: WeaponSlot.Melee,
  heavy: WeaponSlot.Heavy,
  sniper: WeaponSlot.Sniper,
};
const KINDS: readonly FireKind[] = ['hitscan', 'projectile', 'beam', 'melee', 'builder'];

/** Takedowns in one match filtered by weapon slot name, fire kind or weapon id. */
export function killsWith(weaponKills: Record<string, number>, filter: string): number {
  let n = 0;
  const slot = SLOT_BY_NAME[filter];
  for (const [id, k] of Object.entries(weaponKills)) {
    const w = WEAPON_BY_ID[id];
    if (!w || !Number.isFinite(k)) continue;
    if (slot !== undefined ? w.slot === slot : KINDS.includes(filter as FireKind) ? w.kind === filter : id === filter) n += k;
  }
  return n;
}

/**
 * Progress a single match contributes to a quest metric:
 * kills, kills:<slot|kind|weaponId>, headshots, wins, wins:<mode>, matches, mode:<mode>, assists,
 * objectives, voxels, built, collapse, zombies, waves, minutes, score, streak (best in match — the
 * quest keeps the maximum instead of summing, see `isMaxMetric`).
 */
export function questMetricValue(metric: string, s: MatchPlayerStats, mode: ModeId): number {
  const [key, arg] = metric.split(':', 2);
  switch (key) {
    case 'kills':
      return arg ? killsWith(s.weaponKills ?? {}, arg) : s.kills;
    case 'headshots':
      return s.headshots;
    case 'wins':
      return s.won && (!arg || arg === mode) ? 1 : 0;
    case 'matches':
      return 1;
    case 'mode':
      return arg === mode ? 1 : 0;
    case 'assists':
      return s.assists;
    case 'objectives':
      return s.objectives;
    case 'voxels':
      return s.voxelsDestroyed;
    case 'built':
      return s.voxelsBuilt;
    case 'collapse':
      return s.collapseKills;
    case 'zombies':
      return s.zombiesKilled;
    case 'waves':
      return s.wavesSurvived;
    case 'minutes':
      return Math.floor(s.timePlayed / 60);
    case 'score':
      return s.score;
    case 'streak':
      return s.longestStreak;
    default:
      return 0;
  }
}

/** Metrics tracked as "best in a single match" instead of a running total. */
export function isMaxMetric(metric: string): boolean {
  return metric === 'streak' || metric === 'waves';
}

/** New quest progress after a match (clamped to the goal). */
export function advanceQuest(progress: number, goal: number, metric: string, value: number): number {
  const v = Math.max(0, Math.floor(value));
  return Math.min(goal, isMaxMetric(metric) ? Math.max(progress, v) : progress + v);
}
